import { containSize, orientationGeometry } from './geometry.js';
import { sha256Hex } from './hash.js';
import { canonicalizeCaptureMetadata, extractJpegMetadata } from './metadata.js';
import { policyForEvidenceClass } from './policy.js';
import { stripDerivativeColorProfile } from './profile-strip.js';
import type {
  CanonicalCaptureMetadata,
  EvidenceClass,
  MediaPreparationProfile,
  PreparedVariant,
} from './types.js';
import { isAcceptedSourceImageType } from './validation.js';

export interface PreparedBrowserEvidence {
  sourceSha256: string;
  metadata: CanonicalCaptureMetadata;
  variants: readonly PreparedVariant[];
}

/**
 * Browser-only preparation. Canvas re-encoding strips EXIF/GPS and emits orientation=1 pixels.
 * `createImageBitmap(..., imageOrientation:'none')` is used where available; the HTML image
 * fallback is already oriented by modern Safari and therefore skips the manual transform.
 */
let preparationTail: Promise<unknown> = Promise.resolve();
export function prepareEvidenceImage(
  file: Blob,
  evidenceClass: EvidenceClass,
  options: { profile?: MediaPreparationProfile } = {},
): Promise<PreparedBrowserEvidence> {
  const pending=preparationTail.catch(()=>undefined).then(()=>prepareEvidenceImageNow(file,evidenceClass,options));
  preparationTail=pending.catch(()=>undefined);
  return pending;
}
async function prepareEvidenceImageNow(
  file: Blob,
  evidenceClass: EvidenceClass,
  options: { profile?: MediaPreparationProfile } = {},
): Promise<PreparedBrowserEvidence> {
  // Refused before a single byte is decoded: an SVG reaching the canvas would still yield a
  // raster derivative, but a preserved original would carry executable XML into Storage.
  if (!isAcceptedSourceImageType(file.type)) throw new Error('IMAGE_SOURCE_TYPE_UNSUPPORTED');
  const source = new Uint8Array(await file.arrayBuffer());
  const extracted =
    file.type === 'image/jpeg'
      ? extractJpegMetadata(source)
      : { orientation: 1 as const, hadGps: false };
  const decoded = await decodeEvidenceImage(file);
  const canvases: HTMLCanvasElement[] = [];
  try {
  const applyExifTransform = decoded.rawOrientation;
  const geometry = orientationGeometry(
    decoded.width,
    decoded.height,
    applyExifTransform ? extracted.orientation : 1,
  );
  const policy = policyForEvidenceClass(evidenceClass, {
    webpSupported: await canEncode('image/webp'),
    profile: options.profile ?? 'default',
  });
  const variants: PreparedVariant[] = [];

  for (const spec of policy.variants) {
    const size =
      spec.exactWidth && spec.exactHeight
        ? { width: spec.exactWidth, height: spec.exactHeight }
        : containSize(geometry.width, geometry.height, spec.maxLongEdge);
    const canvas = document.createElement('canvas');
    canvases.push(canvas);
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('CANVAS_CONTEXT_UNAVAILABLE');
    const scale = Math.min(size.width / geometry.width, size.height / geometry.height);
    const drawWidth = geometry.width * scale;
    const drawHeight = geometry.height * scale;
    context.fillStyle = '#f5f7fa';
    context.fillRect(0, 0, size.width, size.height);
    context.translate((size.width - drawWidth) / 2, (size.height - drawHeight) / 2);
    context.scale(scale, scale);
    if (applyExifTransform) context.transform(...geometry.transform);
    context.drawImage(decoded.source, 0, 0);
    const qualities = spec.maxBytes
      ? [spec.quality, 0.76, 0.7, 0.64, spec.minimumQuality ?? 0.58].filter(
          (quality, index, values) =>
            quality >= (spec.minimumQuality ?? 0) && values.indexOf(quality) === index,
        )
      : [spec.quality];
    let blob: Blob | null = null;
    for (const quality of qualities) {
      const encoded = await stripDerivativeColorProfile(
        await canvasToBlob(canvas, spec.format, quality),
      );
      if (!spec.maxBytes || encoded.size <= spec.maxBytes) {
        blob = encoded;
        break;
      }
    }
    if (!blob) throw new Error('IMAGE_ENCODE_SIZE_LIMIT');
    variants.push({
      kind: spec.kind,
      mimeType: blob.type,
      width: size.width,
      height: size.height,
      bytes: blob.size,
      sha256: await sha256Hex(blob),
      blob,
    });
  }
  const sourceSha256 = await sha256Hex(source);
  if (policy.preserveOriginal)
    variants.push({
      kind: 'original',
      mimeType: file.type || 'application/octet-stream',
      // The original object retains its encoded pixel matrix. EXIF orientation is applied only
      // to derivatives, so commit-time decoding must compare the original against raw dimensions.
      width: decoded.width,
      height: decoded.height,
      bytes: file.size,
      sha256: sourceSha256,
      blob: file,
    });
  return {
    sourceSha256,
    metadata: canonicalizeCaptureMetadata({
      extracted,
      sourceMimeType: file.type,
      sourceBytes: file.size,
      width: decoded.width,
      height: decoded.height,
    }),
    variants,
  };
  } finally {
    decoded.close?.();
    for(const canvas of canvases){canvas.width=0;canvas.height=0;}
  }
}

export async function decodeEvidenceImage(blob: Blob): Promise<{
  source: CanvasImageSource;
  width: number;
  height: number;
  rawOrientation: boolean;
  close?: () => void;
}> {
  if (typeof globalThis.createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'none' });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        rawOrientation: true,
        close: () => bitmap.close(),
      };
    } catch {
      // Some browsers expose ImageBitmap but reject photos that their image element decodes.
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      rawOrientation: false,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function canEncode(type: string): Promise<boolean> {
  const canvas = document.createElement('canvas');
  return (await canvasToBlob(canvas, type, 0.5)).type === type;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('IMAGE_ENCODE_FAILED'))),
      type,
      quality,
    ),
  );
}
